// Aeonix branding on Misti's screen - two pieces, neither ever in a guest's way.
//
//   EngravedLogo    always on, bottom centre (below the subtitles): "powered by"
//                   and the Aeonix logo, set into the dark screen. The logo keeps
//                   drawing itself on a 7 s loop - outlines traced in the brand
//                   gradient, the colour filling in, a moment of grey - and holds
//                   in colour while Misti speaks.
//
//   BrandInterlude  every 10 minutes while she is resting (idle, drowsy or
//                   asleep), her face steps aside for 3 seconds to a
//                   "made by Aeonix" card, then comes back. Anything that needs
//                   her - her wake word, a line from the phone app, a tap on the
//                   screen - cancels it at once.
//
// Logos: public/brand/, from aeonixinnovations.com.

import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { faceStore } from "./Face/engine/faceStore";
import { FILL, OUTLINES, VIEWBOX } from "./aeonixLogo";

const BRAND_EVERY_MS = 10 * 60 * 1000; // how often the "made by Aeonix" card comes up
const BRAND_SHOW_MS = 3000; // how long it stays
const LEAVE_MS = 600; // the card fading out while her face comes back
const RETRY_MS = 20000; // busy when it was due: try again shortly
const RESTING = new Set(["idle", "drowsy", "asleep"]);

const logo = `${process.env.PUBLIC_URL}/brand/aeonix-logo.png`;

const faceSnapshot = () => faceStore.getSnapshot();

// The Aeonix logo as vectors (aeonixLogo.js, traced from the official logo): a grey
// fill at rest, the brand-gradient fill for when it lights up, and one outline per
// contour for the drawn-in effect.
const AeonixLogo = () => (
	<svg className="brand-logo-svg" viewBox={VIEWBOX} aria-hidden="true">
		<defs>
			<linearGradient id="aeonix-gradient" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2="680">
				<stop offset="0" stopColor="#7f0fe2" />
				<stop offset="0.5" stopColor="#b32b91" />
				<stop offset="1" stopColor="#fb0440" />
			</linearGradient>
		</defs>
		<path className="brand-logo-rest" d={FILL} fillRule="evenodd" />
		<path className="brand-logo-color" d={FILL} fillRule="evenodd" fill="url(#aeonix-gradient)" />
		{OUTLINES.map((d, i) => (
			<path key={i} className="brand-logo-line" d={d} pathLength="1" style={{ "--i": i }} />
		))}
	</svg>
);

// "powered by" and the Aeonix logo, bottom centre. The logo keeps drawing itself,
// on a 7 s loop that is all CSS (HomePage/style.scss): each outline traced in the
// brand gradient, left to right, then the colour flooding in, a few seconds in full
// colour, back to grey for a moment, and again. While Misti speaks it holds still
// in full colour - "powered by" lit while she works - and the loop resumes after.
export const EngravedLogo = () => {
	const { state } = useSyncExternalStore(faceStore.subscribe, faceSnapshot);
	return (
		<div className={`brand-engraved${state === "speaking" ? " is-speaking" : ""}`} aria-hidden="true">
			<span className="brand-powered">powered by</span>
			<span className="brand-glow">
				<AeonixLogo />
			</span>
		</div>
	);
};

/**
 * @param enabled  run the timer at all (after start-up, not in the face demo)
 * @param isBusy   () => true while something is going on the face does not show
 *                 as a state (a line from the app still being prepared, ...)
 * @returns phase: "off" | "on" | "leaving"
 */
export function useBrandInterlude({ enabled, isBusy }) {
	const [phase, setPhase] = useState("off");
	const phaseRef = useRef("off");
	const timers = useRef([]);
	const busyRef = useRef(isBusy);
	busyRef.current = isBusy;

	const set = (p) => {
		phaseRef.current = p;
		setPhase(p);
	};
	const clear = () => {
		timers.current.forEach(clearTimeout);
		timers.current = [];
	};

	const leave = useCallback(() => {
		if (phaseRef.current !== "on") return;
		clear();
		set("leaving");
		timers.current.push(setTimeout(() => set("off"), LEAVE_MS));
	}, []);

	const show = useCallback(() => {
		if (phaseRef.current !== "off") return;
		clear();
		set("on");
		timers.current.push(setTimeout(leave, BRAND_SHOW_MS));
	}, [leave]);

	// The schedule. Only while she is resting and nothing else is going on.
	useEffect(() => {
		if (!enabled) return undefined;
		let timer;
		const due = () => {
			const resting = RESTING.has(faceStore.getSnapshot().state);
			if (resting && !(busyRef.current && busyRef.current())) {
				show();
				timer = setTimeout(due, BRAND_EVERY_MS);
			} else {
				timer = setTimeout(due, RETRY_MS);
			}
		};
		timer = setTimeout(due, BRAND_EVERY_MS);
		return () => clearTimeout(timer);
	}, [enabled, show]);

	// Anything that needs her face cancels the card straight away.
	useEffect(
		() =>
			faceStore.subscribe(() => {
				if (phaseRef.current === "on" && !RESTING.has(faceStore.getSnapshot().state)) leave();
			}),
		[leave]
	);

	// For the face demo and for testing: window.dispatchEvent(new Event("misti:brand")).
	useEffect(() => {
		const onDemand = () => show();
		window.addEventListener("misti:brand", onDemand);
		return () => window.removeEventListener("misti:brand", onDemand);
	}, [show]);

	useEffect(() => clear, []);

	return { phase, show, leave };
}

export const BrandInterlude = ({ phase }) =>
	phase === "off" ? null : (
		<div className={`brand-interlude is-${phase}`} role="img" aria-label="Misti, made in India by Aeonix Innovations">
			<p className="brand-kicker">Misti · Made in India by</p>
			<img className="brand-logo" src={logo} alt="" draggable="false" />
			<p className="brand-site">aeonixinnovations.com</p>
		</div>
	);
